import Bugsnag from 'utils/bugsnag'
import ApiClient from 'api-client/ApiClient'
import ActionsSet from 'core/actions/ActionsSet'
import ListAction from 'core/actions/ListAction'
import UpdateAction from 'core/actions/UpdateAction'
import DataKeys, { entityNamesByKey } from 'k8s/DataKeys'
import { trackEvent } from 'utils/tracking'
import CreateAction from 'core/actions/CreateAction'
import jsYaml from 'js-yaml'
import { ensureArray, pathStr } from 'utils/fp'
import DeleteAction from 'core/actions/DeleteAction'
import { someAsync } from 'utils/async'
import { flatten } from 'ramda'

const { qbert } = ApiClient.getInstance()

const replicaSetActions = ActionsSet.make<DataKeys.ReplicaSets>({
  uniqueIdentifier: 'id',
  indexBy: ['clusterId', 'namespace'],
  entityName: entityNamesByKey[DataKeys.ReplicaSets],
  cacheKey: DataKeys.ReplicaSets,
})

export const listReplicaSets = replicaSetActions.add(
  new ListAction<DataKeys.ReplicaSets, { clusterId: string; namespace?: string }>(
    async (params) => {
      Bugsnag.leaveBreadcrumb('Attempting to get ReplicaSets', params)
      const { clusterId, namespace } = params

      return qbert.getReplicaSets(clusterId, namespace)
    },
  ).addDependency(DataKeys.Pods),
)

export const createReplicaSet = replicaSetActions.add(
  new CreateAction<DataKeys.ReplicaSets, { clusterId: string; namespace: string; yaml: string }>(
    async ({ clusterId, namespace, yaml }) => {
      Bugsnag.leaveBreadcrumb('Attempting to create a new ReplicaSet', {
        clusterId,
        namespace,
        yaml,
      })
      const body = jsYaml.load(yaml)
      const created = await qbert.createReplicaSet(clusterId, namespace, body)
      trackEvent('Create ReplicaSet', {
        id: pathStr('metadata.uid', created),
        name: pathStr('metadata.name', created),
      })
      return created
    },
  ),
)

export const updateReplicaSet = replicaSetActions.add(
  new UpdateAction<
    DataKeys.ReplicaSets,
    {
      clusterId: string
      namespace: string
      name: string
      body: unknown
      requestType: string
      contentType?: string
    }
  >(async ({ clusterId, namespace, name, body, requestType = 'put', contentType }) => {
    Bugsnag.leaveBreadcrumb('Attempting to update a Replica Set', {
      clusterId,
      namespace,
      name,
      body,
    })
    const updateFn = requestType === 'patch' ? qbert.patchReplicaSet : qbert.updateReplicaSet
    const updatedReplicaSet = await updateFn(clusterId, namespace, name, body, contentType)
    trackEvent('Update StatefulSet', { clusterId, namespace, name })
    return updatedReplicaSet
  }),
)

export const deleteReplicaSet = replicaSetActions.add(
  new DeleteAction<
    DataKeys.ReplicaSets,
    { clusterId: string; namespace: string; name: string; id: string }
  >(async ({ clusterId, namespace, name, id }) => {
    Bugsnag.leaveBreadcrumb('Attempting to delete a ReplicaSet', {
      clusterId,
      namespace,
      name,
      id,
    })
    await qbert.deleteReplicaSet(clusterId, namespace, name)
    trackEvent('Delete ReplicaSet', { clusterId, namespace, name, id })
  }),
)

export default replicaSetActions
